<template>
  <div id="map"></div>
</template>

<script setup>
import { onMounted, ref, watch, defineProps } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import markerIcon from '@/assets/marker-icon.png';
import markerShadow from '@/assets/marker-shadow.png';
import html2canvas from 'html2canvas';

// -----------------------------
// Backend proxy endpoints
// -----------------------------
const API_BASE = 'https://geoserver-portal.soilslab.ai';
const WMS_PROXY_URL = `${API_BASE}/wms`;       // Django WMS proxy (GetMap/GetFeatureInfo/etc.)
const LEGEND_PROXY_URL = `${API_BASE}/legend`; // Django legend proxy (returns image/png)

// -----------------------------
// Props
// -----------------------------
const props = defineProps({
  mapCenterCoords: { type: Array },
  selectedSoilProperty: { type: String },
  drawEnabled: { type: Boolean, default: false },
});

// -----------------------------
// State
// -----------------------------
const map = ref(null);
const wmsLayer = ref(null);
const lastMarker = ref(null);
const drawnItems = ref(null);
const drawControl = ref(null);

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// -----------------------------
// Soil layer mapping
// -----------------------------
const soilPropertyToLayer = {
  clay: 'soil_properties:clay_florida_layer',
  cec: 'soil_properties:cec_florida_layer',
  sd: 'soil_properties:sd_florida_layer',
  ph: 'soil_properties:ph_florida_layer',
  soc: 'soil_properties:soc_florida_layer',
  socs: 'soil_properties:socs_florida_layer',
  paw: 'soil_properties:paw_florida_layer',
};

const soilPropertyToUnits = {
  soc: 'g/kg',
  socs: 'tn/ha',
  clay: 'g/kg',
  ph: '',
  sd: 'g/cm³',
  cec: 'g/kg',
  paw: 'm³/m³',
};

const soilPropertyToLabel = {
  soc: 'SOC',
  socs: 'SOCS',
  clay: 'Clay',
  ph: 'pH',
  sd: 'EC', // keep your existing label (even though sd usually means bulk density)
  cec: 'CEC',
  paw: 'PAW',
};

// -----------------------------
// Mounted
// -----------------------------
onMounted(() => {
  map.value = L.map('map', {
    center: [28.45, -85.6],
    zoom: 7,
    minZoom: 2,
    maxBounds: [
      [-90, -180],
      [90, 180],
    ],
  });

  L.Marker.prototype.options.icon = defaultIcon;

  // Basemap
  L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    { attribution: '&copy; <a href="https://www.esri.com/">Esri</a> contributors' }
  ).addTo(map.value);

  // Drawn layers group
  drawnItems.value = new L.FeatureGroup();
  map.value.addLayer(drawnItems.value);

  toggleDrawControl(props.drawEnabled);

  // Initial layer
  changeMapLayer(soilPropertyToLayer.soc);

  // Drawing handler
  map.value.on(L.Draw.Event.CREATED, (event) => {
    try {
      const layer = event.layer;
      drawnItems.value.addLayer(layer);

      const popupContent = `
        <div style="display:flex; justify-content:center; align-items:center; flex-direction:column;">
          <p>Click below to save the data locally.</p>
          <div id="popupSaveButton" class="button-popup">Save</div>
        </div>
      `;
      layer.bindPopup(popupContent).openPopup();

      setTimeout(() => {
        const popupSaveButton = document.getElementById('popupSaveButton');
        if (popupSaveButton) {
          popupSaveButton.addEventListener('click', () => {
            layer.closePopup();
            const polygonBounds = layer.getBounds();
            captureScreenshot(polygonBounds);
          });
        }
      }, 0);

      layer.on('popupclose', () => {
        drawnItems.value.removeLayer(layer);
      });
    } catch (error) {
      console.error('Error during drawing:', error);
    }
  });

  // Click handler for GetFeatureInfo
  map.value.on('click', async (e) => {
    const { lat, lng } = e.latlng;

    if (lastMarker.value) map.value.removeLayer(lastMarker.value);
    lastMarker.value = L.marker([lat, lng]).addTo(map.value);

    const url = getFeatureInfoUrl(map.value, wmsLayer.value, e.latlng, {
      info_format: 'application/json',
    });

    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const properties = data?.features?.[0]?.properties;
      let popupContent = 'There is no available information in this region yet.';

      if (properties?.RED_BAND !== undefined && properties?.RED_BAND !== null) {
        const key = props.selectedSoilProperty || 'soc';
        const label = soilPropertyToLabel[key] ?? key.toUpperCase();
        const units = soilPropertyToUnits[key] ?? '';
        const value = Number(properties.RED_BAND);

        popupContent = `
          <strong>Coordinates:</strong><br>
          Latitude: ${lat.toFixed(4)}<br>
          Longitude: ${lng.toFixed(4)}<br><br>
          <strong>Property:</strong><br>
          ${label}: ${Number.isFinite(value) ? value.toFixed(2) : 'N/A'} ${units ? `(${units})` : ''}<br>
        `;
      }

      lastMarker.value.bindPopup(popupContent).openPopup();
    } catch (error) {
      console.error('Error fetching feature info:', error);
      lastMarker.value.bindPopup('Failed to retrieve data.').openPopup();
    }
  });
});

// -----------------------------
// GetFeatureInfo URL builder
// -----------------------------
function getFeatureInfoUrl(mapObj, layer, latlng, params = {}) {
  if (!layer) return null;

  const point = mapObj.latLngToContainerPoint(latlng, mapObj.getZoom());
  const size = mapObj.getSize();

  const defaultParams = {
    request: 'GetFeatureInfo',
    service: 'WMS',
    srs: 'EPSG:4326',
    styles: '',
    transparent: true,
    version: '1.1.1',
    format: 'image/png',
    bbox: mapObj.getBounds().toBBoxString(),
    height: size.y,
    width: size.x,
    layers: layer.options.layers,
    query_layers: layer.options.layers,
    info_format: 'application/json',
    x: Math.round(point.x),
    y: Math.round(point.y),
  };

  const mergedParams = { ...defaultParams, ...params };
  return layer._url + L.Util.getParamString(mergedParams, layer._url, true);
}

// -----------------------------
// Screenshot capture + logo
// -----------------------------
function captureScreenshot(polygonBounds) {
  const mapElement = document.getElementById('map');
  if (!mapElement || !map.value) return;

  html2canvas(mapElement, { useCORS: true, allowTaint: false, logging: false })
    .then((canvas) => {
      const clippedCanvas = document.createElement('canvas');
      const context = clippedCanvas.getContext('2d');
      if (!context) return;

      const startPoint = map.value.latLngToContainerPoint(polygonBounds.getSouthWest());
      const endPoint = map.value.latLngToContainerPoint(polygonBounds.getNorthEast());
      const width = endPoint.x - startPoint.x;
      const height = startPoint.y - endPoint.y;

      clippedCanvas.width = width;
      clippedCanvas.height = height;

      context.drawImage(canvas, startPoint.x, endPoint.y, width, height, 0, 0, width, height);

      const aspectRatio = height / width;
      let newHeight;
      let newWidth;
      if (height > width) {
        newWidth = 900;
        newHeight = Math.round(newWidth * aspectRatio);
      } else {
        newHeight = 900;
        newWidth = Math.round(newHeight / aspectRatio);
      }

      resizeCanvas(clippedCanvas, newWidth, newHeight, (resizedCanvas) => {
        const logoHeight = Math.floor(resizedCanvas.height / 5);
        const logoWidth = Math.floor((logoHeight * 2) / 5);

        const layerName = soilPropertyToLayer[props.selectedSoilProperty] ?? soilPropertyToLayer.soc;

        // Use the legend proxy (returns image/png)
        const logoUrl = `${LEGEND_PROXY_URL}/?layer=${encodeURIComponent(layerName)}&width=30&height=30`;

        const logo = new Image();
        logo.crossOrigin = 'Anonymous';

        logo.onload = () => {
          const finalCanvas = document.createElement('canvas');
          const finalContext = finalCanvas.getContext('2d');
          if (!finalContext) return;

          finalCanvas.width = resizedCanvas.width + logoWidth;
          finalCanvas.height = Math.max(resizedCanvas.height, logoHeight);

          finalContext.drawImage(resizedCanvas, 0, 0);

          const logoXPosition = resizedCanvas.width;
          const logoYPosition = finalCanvas.height - logoHeight;
          finalContext.drawImage(logo, logoXPosition, logoYPosition, logoWidth, logoHeight);

          const finalImg = finalCanvas.toDataURL('image/png');
          downloadImage(finalImg, `map-${props.selectedSoilProperty || 'soc'}.png`);
        };

        logo.onerror = (error) => {
          console.error('Failed to load the logo.', error);
          alert('Error: Failed to load the logo.');
        };

        logo.src = logoUrl;
      });
    })
    .catch((error) => {
      console.error('Error during screenshot capture:', error);
    });
}

function resizeCanvas(originalCanvas, newWidth, newHeight, callback) {
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = newWidth;
  resizedCanvas.height = newHeight;

  const resizedContext = resizedCanvas.getContext('2d');
  if (!resizedContext) return;

  resizedContext.drawImage(
    originalCanvas,
    0,
    0,
    originalCanvas.width,
    originalCanvas.height,
    0,
    0,
    newWidth,
    newHeight
  );

  callback(resizedCanvas);
}

function downloadImage(uri, filename) {
  const link = document.createElement('a');
  link.href = uri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// -----------------------------
// WMS layer switching
// -----------------------------
function changeMapLayer(layerName) {
  if (!map.value) return;

  if (wmsLayer.value) {
    map.value.removeLayer(wmsLayer.value);
  }

  // IMPORTANT: Use the proxy URL. Leaflet will append params to it.
  wmsLayer.value = L.tileLayer.wms(WMS_PROXY_URL, {
    layers: layerName,
    format: 'image/png',
    transparent: true,
    opacity: 1,
    version: '1.1.1',
  }).addTo(map.value);
}

// -----------------------------
// Watchers
// -----------------------------
watch(
  () => props.mapCenterCoords,
  (newCoords) => {
    if (map.value && newCoords) {
      map.value.setView(newCoords, 11);
    }
  },
  { deep: true }
);

watch(
  () => props.selectedSoilProperty,
  (newProperty) => {
    if (map.value && newProperty && soilPropertyToLayer[newProperty]) {
      changeMapLayer(soilPropertyToLayer[newProperty]);
    }
  }
);

watch(
  () => props.drawEnabled,
  (newValue) => {
    toggleDrawControl(newValue);
  }
);

// -----------------------------
// Draw control toggle
// -----------------------------
function toggleDrawControl(isEnabled) {
  if (!map.value) return;

  if (isEnabled) {
    if (!drawControl.value) {
      drawControl.value = new L.Control.Draw({
        edit: false,
        draw: {
          rectangle: true,
          polygon: true,
          polyline: false,
          circle: false,
          marker: false,
          circlemarker: false,
        },
        position: 'topright',
      });
      map.value.addControl(drawControl.value);
    }
  } else if (drawControl.value) {
    map.value.removeControl(drawControl.value);
    drawControl.value = null;
  }
}
</script>

<style>
html, body, #map {
  position: absolute;
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  z-index: 1;
}

.leaflet-bar.leaflet-draw {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1000;
}

.button-popup {
  background-color: #14213d;
  padding: 10px;
  width: 50px;
  text-align: center;
  color: #ffffff;
  border-radius: 5px;
  cursor: pointer;
}
</style>
